<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4 sm:p-6">
    <div class="w-full max-w-3xl mx-auto">
      <!-- Logo/Title Area -->
      <div class="text-center mb-6 sm:mb-8">
        <h1 class="text-3xl sm:text-4xl font-bold text-white mb-4 sm:mb-6 px-2">{{ demoName }}</h1>
        <a 
          href="mailto:hani@serchplus.io?subject=Search%2B%20Demo%20Inquiry&body=Hi%20Hani%2C%0A%0AI'd%20like%20to%20learn%20more%20about%20Search%2B.%0A%0ABest%20regards"
          class="inline-flex items-center text-sm text-blue-400 hover:text-blue-300 transition-colors duration-300 
                 bg-blue-500/10 hover:bg-blue-500/20 px-3 sm:px-4 py-2 rounded-full border border-blue-400/20
                 mx-2 text-xs sm:text-sm whitespace-normal sm:whitespace-nowrap"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 sm:mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span class="flex-1">Find out how we boost conversions by 30%</span>
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1.5 sm:ml-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </a>
      </div>

      <!-- Search Bar -->
      <div class="relative mb-4">
        <input 
          type="text" 
          placeholder="What are you looking for?"
          class="w-full px-4 sm:px-6 py-3 sm:py-4 bg-white bg-opacity-10 rounded-xl text-white placeholder-gray-400 
                 border border-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 
                 focus:outline-none transition-all duration-300
                 text-base sm:text-lg backdrop-blur-sm pr-12"
          v-model="searchQuery"
          @keyup.enter="handleSearch"
          :disabled="isLoading"
        >
        <button 
          @click="handleSearch"
          class="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 
                 bg-blue-600 hover:bg-blue-700 text-white p-1.5 sm:p-2 rounded-lg
                 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="isLoading"
        >
          <div v-if="isLoading" class="animate-spin h-5 w-5 sm:h-6 sm:w-6 border-2 border-white border-t-transparent rounded-full"></div>
          <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button>
      </div>

      <!-- Quick Search Buttons -->
      <div v-if="!searchResult && questions.length > 0" class="mt-6 sm:mt-8 text-center px-2">
        <div class="text-gray-400 mb-2 sm:mb-3 text-sm">Popular searches</div>
        <div class="flex flex-wrap justify-center gap-2">
          <button 
            v-for="question in questions" 
            :key="question"
            @click="searchQuery = question; handleSearch()"
            class="px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white bg-opacity-5 text-gray-300 
                   hover:bg-opacity-10 transition-all duration-300 text-xs sm:text-sm
                   break-words max-w-full"
          >
            {{ question }}
          </button>
        </div>
      </div>

      <!-- Search Results -->
      <div v-if="searchResult" class="mt-8 sm:mt-12 bg-white bg-opacity-5 rounded-xl sm:rounded-2xl p-4 sm:p-6 backdrop-blur-sm">
        <h2 class="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">{{ searchResult.recommendations.tailored_headline }}</h2>
        
        <!-- Product Card -->
        <div class="mb-6 sm:mb-8">
          <a :href="`${demoDomain}${searchResult.recommendations.product_url}`" 
             class="text-lg sm:text-xl text-blue-400 hover:text-blue-300 transition-colors duration-300 flex items-start sm:items-center flex-wrap gap-2"
             target="_blank"
             rel="noopener noreferrer">
            <span class="flex-1">{{ searchResult.recommendations.product_name }}</span>
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>

        <!-- Benefits List -->
        <ul class="space-y-3 sm:space-y-4">
          <li v-for="(benefit, index) in searchResult.recommendations.tailored_benefits" 
              :key="index"
              class="flex items-start text-sm sm:text-base text-gray-300">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 sm:h-6 sm:w-6 mr-2 sm:mr-3 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            <span class="flex-1">{{ benefit }}</span>
          </li>
        </ul>

        <!-- New Search Button -->
        <button 
          @click="resetSearch"
          class="mt-6 sm:mt-8 text-gray-400 hover:text-white transition-colors duration-300 flex items-center mx-auto text-sm sm:text-base"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
          </svg>
          Try another search
        </button>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="mt-6 text-red-400 text-center">
        {{ error }}
      </div>

      <!-- Trust Indicators -->
      <div v-if="!searchResult" class="mt-8 sm:mt-12">
        <div class="flex flex-col sm:flex-row justify-center items-stretch gap-3 sm:gap-4 px-2">
          <div class="flex items-center bg-gradient-to-r from-blue-500/10 to-blue-600/10 rounded-lg p-3 sm:px-4 sm:py-2 border border-blue-500/20">
            <div class="text-blue-400 mr-2 sm:mr-3">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <div>
              <div class="text-blue-400 font-bold text-sm sm:text-base">Natural</div>
              <div class="text-blue-300/70 text-xs">Language Search</div>
            </div>
          </div>
          
          <div class="flex items-center bg-gradient-to-r from-green-500/10 to-green-600/10 rounded-lg p-3 sm:px-4 sm:py-2 border border-green-500/20">
            <div class="text-green-400 mr-2 sm:mr-3">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <div>
              <div class="text-green-400 font-bold text-sm sm:text-base">+30%</div>
              <div class="text-green-300/70 text-xs">More Conversions</div>
            </div>
          </div>

          <div class="flex items-center bg-gradient-to-r from-purple-500/10 to-purple-600/10 rounded-lg p-3 sm:px-4 sm:py-2 border border-purple-500/20">
            <div class="text-purple-400 mr-2 sm:mr-3">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div>
              <div class="text-purple-400 font-bold text-sm sm:text-base">AI Powered</div>
              <div class="text-purple-300/70 text-xs">Personalisation</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { db, functions } from '@/config/firebase'
import { doc, getDoc, updateDoc, increment } from 'firebase/firestore'
import { useAuthStore } from '@/stores/auth'

const route = useRoute()
const getDemoRecommendations = httpsCallable(functions, 'getDemoRecommendations')

const searchQuery = ref('')
const isLoading = ref(false)
const error = ref('')
const searchResult = ref<any>(null)
const demoName = ref('')
const demoDomain = ref('')
const questions = ref<string[]>([])

onMounted(async () => {
  // Send heartbeat to warm up function
  getDemoRecommendations({ org_id: route.params.id, is_heartbeat: true })
    .catch(err => console.error('Heartbeat failed:', err))

  const authStore = useAuthStore()
  const demoId = route.params.id as string
  const demoRef = doc(db, 'demos', demoId)
  const demoDoc = await getDoc(demoRef)
  
  if (demoDoc.exists()) {
    const demo = demoDoc.data()
    demoName.value = demo.name || demoId
    demoDomain.value = demo.shopify_domain
    questions.value = demo.questions || []

    // Increment views if user is not authenticated
    if (!authStore.user) {
      try {
        // Get current document to check if views exists
        const currentDoc = await getDoc(demoRef)
        const currentViews = currentDoc.data()?.views

        await updateDoc(demoRef, {
          views: currentViews === undefined ? 1 : currentViews + 1
        })
      } catch (err) {
        console.error('Failed to increment views:', err)
      }
    }
  } else {
    error.value = 'Demo not found'
  }
})

const handleSearch = async () => {
  if (!searchQuery.value.trim()) return
  
  isLoading.value = true
  error.value = ''
  searchResult.value = null

  try {
    const authStore = useAuthStore()
    const result = await getDemoRecommendations({
      org_id: route.params.id,
      q: searchQuery.value,
      is_test: authStore.user ? true : undefined
    })

    searchResult.value = result.data
  } catch (err) {
    error.value = 'Something went wrong. Please try again later.'
    console.error('Search error:', err)
  } finally {
    isLoading.value = false
  }
}

const resetSearch = () => {
  searchQuery.value = ''
  searchResult.value = null
  error.value = ''
}
</script>

<style scoped>
input::placeholder {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

input:focus::placeholder {
  opacity: 0.4;
}
</style>
